import { CONTENT_TEAM_ASSIGNMENT, TEAMS_CONTENT_PARTITIONING } from 'EmailData/constants/teams';
import I18n from 'I18n';
import PortalIdParser from 'PortalIdParser';
import { applySearchCRMRequestOptionsChanges } from 'email-management-lib/Shared/apis/crmSearch';
import { MARKETING_EMAIL_OBJECT_TYPE } from 'email-management-lib/Shared/constants/frameworkTable';
import { assignUsersAndTeams } from 'email-management-lib/refactor/actions/manage/emailActions';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { useFetchTeamPermissions, useFetchTeams, useFetchUserPermissions, useFetchUsers } from 'framework-asset-management-lib';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import { List } from 'immutable';
import useUserInfo from 'messaging-management-lib/user/hooks/useUserInfo';
import Raven from 'raven-js';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import useBulkManageAccess from './useBulkManageAccess';
export default function useManageAccessPanel({
  isAssigningAllInCurrentView,
  contentIds,
  objectIds,
  onClose,
  onSuccess,
  totalObjects
}) {
  var _crmObjectTypeListing;
  const dispatch = useDispatch();
  const {
    userInfo
  } = useUserInfo();
  const currentUserId = userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.user_id;
  const {
    loading: teamsLoading,
    error: teamsError,
    teams
  } = useFetchTeams({
    viewAllAssetsScope: CONTENT_TEAM_ASSIGNMENT,
    viewTeamOwnedAssetsScope: TEAMS_CONTENT_PARTITIONING
  });
  const {
    loading: usersLoading,
    error: usersError,
    users
  } = useFetchUsers({
    viewAllAssetsScope: CONTENT_TEAM_ASSIGNMENT,
    viewTeamOwnedAssetsScope: TEAMS_CONTENT_PARTITIONING
  });
  const {
    loading: teamIdsLoading,
    teamIds,
    error: teamIdsError
  } = useFetchTeamPermissions({
    objectType: MARKETING_EMAIL_OBJECT_TYPE,
    objectIdOrIds: objectIds
  });
  const {
    startBulkManageAccess
  } = useBulkManageAccess();
  const crmObjectTypeListingFilters = useCrmObjectTypeListingFilters();
  const hiddenFilters = ((_crmObjectTypeListing = crmObjectTypeListingFilters.hiddenFilter) === null || _crmObjectTypeListing === void 0 ? void 0 : _crmObjectTypeListing.filterGroups[0].filters) || [];
  const filterGroups = crmObjectTypeListingFilters.filters.filterGroups.map(filterGroup => {
    const filters = [...filterGroup.filters, ...hiddenFilters, ...crmObjectTypeListingFilters.filters.quickFilters];
    return {
      filters
    };
  });
  const {
    loading: userIdsLoading,
    userIds,
    error: userIdsError
  } = useFetchUserPermissions({
    objectType: MARKETING_EMAIL_OBJECT_TYPE,
    objectIdOrIds: objectIds
  });
  const handleSave = useCallback(({
    selectedTeamIds,
    selectedUserIds
  }) => {
    const assignBulkPermissionsRequestBody = {
      crmSearchRequest: applySearchCRMRequestOptionsChanges({
        // @ts-expect-error missing offset, requestOptions, sorts but not needed for this request
        requestOptions: {
          count: totalObjects,
          objectTypeId: MARKETING_EMAIL_OBJECT_TYPE,
          filterGroups,
          query: crmObjectTypeListingFilters.filters.query
        },
        currentUserId: currentUserId
      }),
      userId: currentUserId,
      portalId: PortalIdParser.get(),
      permissionRefIdsByType: {
        USER: selectedUserIds,
        TEAM: selectedTeamIds
      }
    };
    const saveOptions = {
      fetchEmails: false
    };
    if (isAssigningAllInCurrentView) {
      void startBulkManageAccess({
        variables: {
          requestBody: assignBulkPermissionsRequestBody
        },
        onCompleted: () => {
          FloatingAlertStore.addAlert({
            type: 'info',
            titleText: I18n.text(`email-management.assignTeams.bulkSuccess.message`),
            message: I18n.text(`email-management.assignTeams.bulkSuccess.title`, {
              count: totalObjects
            })
          });
          onClose();
        },
        onError: error => {
          Raven.captureException(error);
        }
      });
    } else {
      dispatch(assignUsersAndTeams(List(contentIds), {
        users: selectedUserIds,
        teams: selectedTeamIds
      }, saveOptions)).then(() => {
        onClose();
        onSuccess({
          objectIdOrIds: objectIds,
          selectedTeamIds,
          selectedUserIds
        });
      }).catch(e => Raven.captureException(e));
    }
  }, [totalObjects, filterGroups, crmObjectTypeListingFilters.filters.query, currentUserId, isAssigningAllInCurrentView, startBulkManageAccess, onClose, dispatch, contentIds, onSuccess, objectIds]);
  const isLoading = useMemo(() => teamIdsLoading || userIdsLoading || teamsLoading || usersLoading, [teamIdsLoading, userIdsLoading, teamsLoading, usersLoading]);
  const hasErrors = useMemo(() => teamIdsError || userIdsError || teamsError || usersError, [teamIdsError, userIdsError, teamsError, usersError]);
  return useMemo(() => ({
    teamIds,
    userIds,
    hasErrors,
    isLoading,
    handleSave,
    users,
    teams
  }), [teamIds, userIds, hasErrors, isLoading, handleSave, users, teams]);
}